import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TeamsService } from '../../services/teams/teams.service';
import { SnackBarComponent } from '../snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-team-member-selector',
    templateUrl: './team-member-selector.component.html',
    styleUrls: ['./team-member-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamMemberSelectorComponent implements OnInit, OnChanges {
  @Input() team: any;
  @Input() multiple = true;
  @Input() allowPendingSignupAllowance = false;
  @Input() isP2p = false;
  @Input() toWallit = false;
  @Input() usingWallit = false;
  @Input() requireActiveMembership = false;
  @Output() membersSelectedEvent: EventEmitter<any> = new EventEmitter<any>();

  public members: Array<any> | undefined;
  public filteredMembers: Array<any> = [];
  private searchValue = '';

  constructor(private teamsService: TeamsService, private snackbar: MatSnackBar) {}

  ngOnInit(): void {
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
      const thisTeam = this.team ? this.team : wellspace;
      this.teamsService.getTeamMembers(thisTeam?.id).then((members) => {
        this.teamsService.buildMemberList(members, thisTeam);
        this.members = members;
        this.filteredMembers = JSON.parse(JSON.stringify(this.members));
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.members) {
      return;
    }
    if (!changes['isP2p'] && !changes['toWallit'] && !changes['usingWallit']) {
      return;
    }

    const ineligibleSelectedMembers = this.members.filter(
      (member: any) => member.selected && this.getIneligibleMessage(member),
    );
    if (ineligibleSelectedMembers.length === 0) {
      return;
    }

    ineligibleSelectedMembers.forEach((member: any) => (member.selected = false));
    this.membersUpdated();
  }

  private membersUpdated(): void {
    this.filteredMembers = JSON.parse(
      JSON.stringify(
        this.members?.filter(
          (member: any) => !this.searchValue || this.getMemberEmail(member).indexOf(this.searchValue) >= 0,
        ),
      ),
    );
    this.membersSelectedEvent.emit(this.members?.filter((member: any) => member.selected));
  }

  memberCheckboxClicked(event: any, filteredMember: any): void {
    const ineligibleMessage = this.getIneligibleMessage(filteredMember);
    if (ineligibleMessage) {
      this.snackbar.openFromComponent(SnackBarComponent, {
        data: ineligibleMessage,
      });
      event.preventDefault();
      return;
    }
    if (!this.multiple) {
      this.members?.forEach((thisMember) => (thisMember.selected = false));
    }
    const member = this.members?.find(
      (thisMember: any) => this.getTeamUserId(thisMember) === this.getTeamUserId(filteredMember),
    );
    if (!member) {
      return;
    }
    member.selected = member.selected ? false : true;
    this.membersUpdated();
  }

  private isEligibleMember(member: any): boolean {
    return !this.getIneligibleMessage(member);
  }

  private getIneligibleMessage(member: any): string {
    if (!this.getTeamUserId(member)) {
      return 'Unable to identify this team member. Please refresh and try again.';
    }
    if (this.requireActiveMembership && this.isRemovedOrDisabled(member)) {
      return 'This team member is not eligible for one time rewards.';
    }
    if (this.requiresSignedUpUser() && !member.userId) {
      return 'You can only send payments to users who have already signed up.';
    }
    if (this.usingWallit && member.userId === this.team.ownerUserId) {
      return 'You cannot send rewards to the team owner using Wallit Balance.';
    }
    if (!this.requiresSignedUpUser() && !this.canReceiveStripeAllowance(member)) {
      return 'This team member is not eligible for one time allowance rewards.';
    }
    return '';
  }

  private requiresSignedUpUser(): boolean {
    return !this.allowPendingSignupAllowance || this.isP2p || this.toWallit || this.usingWallit;
  }

  private canReceiveStripeAllowance(member: any): boolean {
    return !this.isRemovedOrDisabled(member);
  }

  private isRemovedOrDisabled(member: any): boolean {
    return !!member?.disabled || this.getRemovalStatus(member) === 'deactivationPendingSettlement';
  }

  private getRemovalStatus(member: any): string {
    return (
      member?.removalStatus ||
      member?.metadata?.removalStatus ||
      ''
    );
  }

  private getTeamUserId(member: any): string {
    return member?.memberId || member?.teamUserId || member?.id || '';
  }

  private getMemberEmail(member: any): string {
    return member?.email || member?.userEmail || '';
  }

  private getEligibleMembers(): any[] {
    return this.members?.filter((member) => this.isEligibleMember(member)) || [];
  }

  selectAll(): void {
    const eligibleMembers = this.getEligibleMembers();
    eligibleMembers.forEach((member: any) => (member.selected = true));
    this.membersUpdated();
  }

  deselectAll(): void {
    this.members?.forEach((member: any) => (member.selected = false));
    this.membersUpdated();
  }

  filterItems(event: any): void {
    this.searchValue = event.target.value;
    this.membersUpdated();
  }
}
